import React, { useEffect, useMemo, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import BaseSelect, { SelectChangeEvent } from '@mui/material/Select';

export interface SelectProps {
  options: any[];
  value: any;
  onChange: (val: any) => void;
  hasNone?: boolean;
  isIndicator?: boolean;
  disabled?: boolean;
  controlHeight?: number;
  selectFontSize?: number;
  selectFontWeight?: number;
  selectColor?: string;
}

export default function Select({
  options,
  value,
  onChange,
  hasNone,
  isIndicator,
  disabled,
  controlHeight,
  selectFontSize = 13,
  selectFontWeight = 500,
  selectColor = '#33485e',
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const handleChange = (event: SelectChangeEvent) => {
    if (disabled) {
      return;
    }
    onChange(event.target.value);
  };
  const handleOpen = () => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const selectSx = useMemo(() => {
    if (controlHeight) {
      return {
        textAlign: 'right',
        padding: '4px',
        height: `${controlHeight}px`,
        '& .MuiOutlinedInput-root': {
          height: `${controlHeight}px`,
          minHeight: `${controlHeight}px`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          background: 'var(--background-chart)',
        },
        '& .MuiSelect-select': {
          paddingTop: '6px',
          paddingBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          fontSize: `${selectFontSize}px`,
          fontWeight: selectFontWeight,
          color: selectColor,
        },
        '& .MuiSelect-icon': {
          right: 3,
          top: 9,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          top: 0,
          height: `${controlHeight}px`,
          borderRadius: '5px',
          border: `1px solid ${isOpen ? 'var(--filter-bar-select-border-active)' : '#99a3af'}`,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': disabled
          ? {}
          : {
              borderRadius: '5px',
              border: '1px solid var(--filter-bar-select-border-active)',
            },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderRadius: '5px',
          border: `1px solid ${isOpen ? 'var(--filter-bar-select-border-active)' : '#99a3af'}`,
        },
      } as const;
    }

    return {
      textAlign: 'right',
      padding: '4px',
      '& .MuiSelect-icon': {
        right: 3,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #99a3af',
        borderBottom: isOpen ? 'none' : undefined,
        borderRadius: '5px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--filter-bar-select-border-active)',
        borderBottom: isOpen ? 'none' : undefined,
        borderRadius: '5px',
        borderBottomLeftRadius: isOpen ? 0 : undefined,
        borderBottomRightRadius: isOpen ? 0 : undefined,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${isOpen ? 'var(--filter-bar-select-border-active)' : '#99a3af'}`,
        borderBottom: isOpen ? 'none' : undefined,
        borderRadius: '5px',
        borderBottomLeftRadius: isOpen ? 0 : undefined,
        borderBottomRightRadius: isOpen ? 0 : undefined,
      },
      '& .MuiSelect-select': {
        paddingTop: '5px',
        paddingBottom: '5px',
        fontSize: `${selectFontSize}px`,
        fontWeight: selectFontWeight,
        color: selectColor,
      },
    } as const;
  }, [controlHeight, disabled, isOpen, selectColor, selectFontSize, selectFontWeight]);

  return (
    <FormControl
      sx={{
        m: controlHeight ? 0 : 1,
        minWidth: isIndicator ? 84 : 81,
        maxWidth: isIndicator ? 84 : 81,
        ...(controlHeight
          ? {
              height: `${controlHeight}px`,
            }
          : {}),
      }}
      size={controlHeight ? 'small' : 'medium'}
      disabled={disabled}
    >
      <BaseSelect
        labelId="demo-select-small-label"
        id="demo-select-small"
        value={value}
        label="Age"
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        input={<OutlinedInput />}
        displayEmpty
        disabled={disabled}
        sx={selectSx}
        MenuProps={{
          elevation: 0,
          PaperProps: {
            sx: {
              backgroundColor: 'var(--background-chart)',
              border: '1px solid var(--filter-bar-select-border-active)',
              borderTop: 'none',
              marginTop: controlHeight ? '-10px' : isIndicator ? '-20px' : '-2px',
              marginLeft: isIndicator ? '-1px' : '0px',
              borderRadius: '5px',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              boxShadow: '-moz-initial',
              '& .MuiMenuItem-root': {
                justifyContent: 'flex-end !important',
                fontSize: `${selectFontSize}px`,
                fontWeight: selectFontWeight,
                color: selectColor,
              },
            },
          },
        }}
      >
        {hasNone && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {options.map((t: number) => (
          <MenuItem value={t} key={t}>
            {t}
          </MenuItem>
        ))}
      </BaseSelect>
    </FormControl>
  );
}
